import { Operation, Editor, Node } from 'domains/reporter/RichTextEditor/core';
import { withInlineTemplate } from '../common/inlineTemplate';
import type { CreateEnhanceEditorState } from '../../types';
import { walkSlateContent, createEditorApplyOverrideNotification } from '../../utils';
import clone from 'ramda/es/clone';
import { insertPicklist } from './utils';
import { PICKLIST_PLUGIN_ID } from './types';
import type { PicklistPluginElement, PicklistPluginPropertyOptions } from './types';
import { Element } from 'slate';

export const enhanceEditorStatePicklist: CreateEnhanceEditorState<PicklistPluginPropertyOptions> =
  (options) => (editor: Editor) => {
    const { apply, insertFragment, deleteFragment, setFragmentData } = editor;

    editor.apply = (operation: Operation) => {
      // If any node is set to be a picklist give it an ID and call insert picklist
      if (operation?.type === 'set_node' && operation.newProperties.type === options.pluginID) {
        createEditorApplyOverrideNotification(options.pluginID, operation);

        const { children, ...picklistProps } = insertPicklist();
        operation.newProperties = picklistProps;
        options.onPicklistInsert?.(operation.newProperties);
      }

      apply(operation);
    };

    // If we are pasting check the fragment for picklists
    // If we are pasting a picklist then generate it a new ID and
    // clone the options
    editor.insertFragment = (fragment: Node[]) => {
      const newFragment = clone(fragment);

      const onElement = (node: Node) => {
        if (node.type === options.pluginID) {
          const newPicklist = options.onPicklistCopy?.(node);

          if (newPicklist) {
            node.picklistID = newPicklist.id;
          }
        }
      };

      walkSlateContent(onElement)(newFragment);

      return insertFragment(newFragment);
    };

    editor.deleteFragment = () => {
      if (editor.selection && options.onPicklistRemove) {
        const picklists = Editor.nodes(editor, {
          at: editor.selection,
          match: (n) => Element.isElement(n) && n.type === PICKLIST_PLUGIN_ID,
          mode: 'all',
        });
        Array.from(picklists).forEach((picklistNode) => {
          options.onPicklistRemove?.(picklistNode[0]);
        });
      }
      return deleteFragment();
    };

    editor.setFragmentData = (data: DataTransfer) => {
      // If the editor wasn't configured with a copy handler or there's no selection,
      // skip the picklist bookkeeping and let Slate handle the data as usual.
      if (!options.onPicklistFragmentCopy || editor.selection == null) {
        return setFragmentData(data);
      }

      // Walk the copied fragment to determine which picklist IDs are included so the
      // clipboard cache can persist their options across refreshes/other editors.
      const fragment = Editor.fragment(editor, editor.selection);
      const picklistIDs = new Set<string>();
      walkSlateContent((node: Node) => {
        if (Element.isElement(node) && node.type === options.pluginID && node.picklistID) {
          picklistIDs.add(node.picklistID);
        }
      })(fragment);

      if (picklistIDs.size > 0) {
        options.onPicklistFragmentCopy([...picklistIDs]);
      }

      return setFragmentData(data);
    };

    return withInlineTemplate<PicklistPluginElement>({
      pluginID: options.pluginID,
      createNode: insertPicklist,
      onNodeCreate: options.onPicklistInsert,
      onNodeRemove: options.onPicklistRemove,
    })(editor);
  };
